import Tippy from '@tippyjs/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';

import BaseTable, { BaseTableCellContextType } from 'components/common/baseTable/BaseTable';

//Highcharts component to render the chart inside the tooltip
const LineChartInTooltip = ({ trendData }: { trendData: number[] }) => {
  const chartOptions = {
    chart: {
      type: 'line',
      height: 100,
      width: 200,
    },
    title: {
      text: 'Weekly Trend',
      style: {
        fontSize: 12,
      },
    },
    xAxis: {
      visible: false,
      categories: ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5'], //mocked categories
    },
    yAxis: {
      visible: false,
    },
    plotOptions: {
      series: {
        animation: {
          duration: 1000,
        },
        clip: false,
        crisp: true,
        shadow: false,
        states: {
          hover: {
            lineWidthPlus: 1,
            marker: {
              radius: 5,
            },
          },
        },
      },
    },
    series: [
      {
        name: 'Weekly Trend',
        data: trendData,
        color: '#d425a3',
      },
    ],
    credits: {
      enabled: false,
    },
    accessibility: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      headerFormat: '',
      pointFormat:
        '<span style="color:{point.color}">●</span>{point.category}: <b>{point.y}</b><br/>',
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

interface EngagementMetrics {
  current: number;
  change: number;
  weeklyTrend: number[];
}

interface Account {
  name: string;
  domain: string;
  qualificationScore: number;
  pipelinePredictScore: number;
  uniqueVisitors: EngagementMetrics;
  pageVisits: EngagementMetrics;
  impressions: EngagementMetrics;
  salesEngagementMins: EngagementMetrics;
}

const Bottom10EngagingAccounts = ({ data }: { data: Account[] }): React.ReactElement => {
  const columnDefinitions = [
    {
      accessorKey: 'name',
      header: 'Name',
      minSize: 120,
    },
    {
      accessorKey: 'domain',
      header: 'Domain',
    },
    {
      accessorKey: 'qualificationScore',
      header: 'Qualification Score',
    },
    {
      accessorKey: 'pipelinePredictScore',
      header: 'Pipeline Predict Score',
    },
    {
      accessorKey: 'uniqueVisitors',
      header: 'Unique Visitors (Last 1 Week)',
      cell: ({ getValue }: BaseTableCellContextType) => {
        const value = getValue() as EngagementMetrics;
        return (
          <Tippy
            content={<LineChartInTooltip trendData={value.weeklyTrend} />}
            delay={500}
            interactive={true}
            className={'account-list-table-tooltip'}
          >
            <span style={{ color: value.change > 0 ? 'green' : 'red' }}>
              {value.current}{' '}
              <sup>{value.change > 0 ? `+${value.change}%` : `${value.change}%`}</sup>
            </span>
          </Tippy>
        );
      },
    },
    {
      accessorKey: 'pageVisits',
      header: 'Page Visits (Last 1 Week)',
      cell: ({ getValue }: BaseTableCellContextType) => {
        const value = getValue() as EngagementMetrics;
        return (
          <Tippy
            content={<LineChartInTooltip trendData={value.weeklyTrend} />}
            delay={500}
            interactive={true}
            className={'account-list-table-tooltip'}
          >
            <span style={{ color: value.change > 0 ? 'green' : 'red' }}>
              {value.current}{' '}
              <sup>{value.change > 0 ? `+${value.change}%` : `${value.change}%`}</sup>
            </span>
          </Tippy>
        );
      },
    },
    {
      accessorKey: 'impressions',
      header: '# of Impressions',
      cell: ({ getValue }: BaseTableCellContextType) => {
        const value = getValue() as EngagementMetrics;
        return (
          <Tippy
            content={<LineChartInTooltip trendData={value.weeklyTrend} />}
            delay={500}
            interactive={true}
            className={'account-list-table-tooltip'}
          >
            <span style={{ color: value.change > 0 ? 'green' : 'red' }}>
              {value.current}{' '}
              <sup>{value.change > 0 ? `+${value.change}%` : `${value.change}%`}</sup>
            </span>
          </Tippy>
        );
      },
    },
    {
      accessorKey: 'salesEngagementMins',
      header: '# of Sales Engagement Minutes',
      cell: ({ getValue }: BaseTableCellContextType) => {
        const value = getValue() as EngagementMetrics;
        return (
          <Tippy
            content={<LineChartInTooltip trendData={value.weeklyTrend} />}
            delay={500}
            interactive={true}
            className={'account-list-table-tooltip'}
          >
            <span style={{ color: value.change > 0 ? 'green' : 'red' }}>
              {value.current}{' '}
              <sup>{value.change > 0 ? `+${value.change}%` : `${value.change}%`}</sup>
            </span>
          </Tippy>
        );
      },
    },
  ];

  return (
    <BaseTable
      data={data}
      columnDefinitions={columnDefinitions}
      initialPageSize={5}
      title={'Bottom 10% Engaging Accounts'}
    />
  );
};

export default Bottom10EngagingAccounts;
